import { Controller } from "@hotwired/stimulus"
import { loadTileSet } from "../pixel_map/tile_set"

// Draws the pixel world map for `UI::PixelMapComponent`. The component knows
// nothing about events: it is handed points (a place, a sprite, a label, a
// link) and the address of the tile set, and this controller turns them into a
// map a reader can pan, zoom and click. ADR 0008.
//
// **The map is an addition, never the content.** Every point is also on the
// page as text, so a map that never draws (no script, a tile set that failed
// to load, a closed panel) costs the reader a picture and nothing else. That
// is why a failure here is a sentence in the status line and not an error.
//
// The page talks to the map through two window events rather than through a
// reference to it, so the list beside the map need not know the map exists:
//
// - `pixel-map:highlight`, `{ id }` or `{ id: null }`: light that event's pin,
//   or the bubble it is in, while the reader points at it in the list.
// - `pixel-map:selected`, dispatched on this element: the reader clicked a
//   pin, and its card is open.
export default class extends Controller {
  static targets = ["canvas", "zoomIn", "zoomOut", "card", "tip", "status", "points"]
  static values = { tiles: String, label: String, focus: String }

  connect() {
    this.highlighter = (event) => this.map?.highlight(event.detail?.id ?? null)
    window.addEventListener("pixel-map:highlight", this.highlighter)
    this.#start()
  }

  disconnect() {
    window.removeEventListener("pixel-map:highlight", this.highlighter)
    this.connected = false
    this.map?.destroy()
    this.map = null
  }

  // Asked for by the page when the panel holding the map changes size in a
  // way the map cannot observe, such as being shown again.
  resize() {
    this.map?.resize()
  }

  select(id) {
    this.map?.select(id)
  }

  // Centre on one event, or with no id go back to every event. Called by the
  // page when the reader opens an event beside the map, which keeps its place.
  focus(id) {
    this.focusValue = id || ""
    this.map?.focus(id || null)
  }

  async #start() {
    this.connected = true
    // The map's engine is its own file, fetched only by a page that draws a
    // map, so every other page's bundle carries none of it (ADR 0008). esbuild
    // splits it out because of this import, and Propshaft serves the chunk
    // under the digest esbuild already gave it.
    const [{ mountPixelMap }, tileSet] = await Promise.all([
      import("../pixel_map/map"),
      loadTileSet(this.tilesValue)
    ])
    // A Turbo visit can take the element away while the tile set loads.
    if (!this.connected) return
    if (!tileSet) return this.#fail()

    this.map = mountPixelMap(this.element, {
      canvas: this.canvasTarget,
      zoomIn: this.hasZoomInTarget ? this.zoomInTarget : null,
      zoomOut: this.hasZoomOutTarget ? this.zoomOutTarget : null,
      card: this.cardTarget,
      tip: this.hasTipTarget ? this.tipTarget : null,
      tileSet,
      base: this.tilesValue,
      points: this.#points(),
      focus: this.focusValue || null,
      onSelect: (id) => this.dispatch("selected", { prefix: "pixel-map", detail: { id } }),
      onError: () => this.#fail()
    })
    if (this.element.dataset.debug !== undefined) window.pixelMap = this.map
  }

  #points() {
    if (!this.hasPointsTarget) return []

    try {
      const points = JSON.parse(this.pointsTarget.textContent)
      return Array.isArray(points) ? points : []
    } catch {
      return []
    }
  }

  #fail() {
    this.map?.destroy()
    this.map = null
    this.element.classList.add("pixel-map--failed")
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = "The map could not be loaded. Every event is in the list."
    this.statusTarget.hidden = false
  }
}
